/**
 * Ascension ladder (v0.60, reworked in v0.61 per Javi): beating the run
 * on ascension N unlocks N+1. Injury steps now alternate with −5 max HP
 * steps, and the top levels attack the run's foundations:
 *   A1 injuries (classic) · A2 −5 HP + mission pressure ×1.1
 *   A3 whole-node injuries · A4 −5 HP + act heal capped at 80%
 *   A5 whole-run injuries · A6 −5 HP + an elite-gauntlet row per act
 *   A7 start injured · A8 −5 HP + mission pressure to +20% total
 *   A9 stacking injuries · A10 one fewer starting creature (A7/A9
 *   swapped in v1.75, per Javi)
 * (the HP-step riders were added in v0.80 per Javi)
 * Every level keeps all the modifiers below it. Numeric rules live here
 * (pure data, shared with the sims); the injury clear/stack thresholds
 * are consumed by utils/battle.ts.
 */
export const MAX_ASCENSION = 10

export interface AscensionDef {
  level: number
  name: string
  /** Player-facing text for THIS level's new modifier. */
  blurb: string
}

export const ASCENSIONS: AscensionDef[] = [
  {
    level: 0,
    name: 'Normal',
    blurb: 'The standard run. Winning unlocks ASCENSIONS — and each ascension you conquer adds new TRAINED SKILLS to the curriculum.',
  },
  {
    level: 1,
    name: 'Ascension 1 — Bruises',
    blurb: 'Failed missions INJURE the whole team (−10% to every element). A creature shakes its injury off with its next successful mission, and any that linger heal when the node ends.',
  },
  {
    level: 2,
    name: 'Ascension 2 — Thin Rations',
    blurb: 'Start with −5 max HP, and every mission runs slightly tougher (+10% requirements and damage).',
  },
  {
    level: 3,
    name: 'Ascension 3 — Deep Wounds',
    blurb: 'Injuries last the WHOLE NODE — successes no longer clear them. They heal when the node ends.',
  },
  {
    level: 4,
    name: 'Ascension 4 — Meager Supplies',
    blurb: 'Another −5 max HP (−10 total), and completing an act only heals you up to 80% of max HP.',
  },
  {
    level: 5,
    name: 'Ascension 5 — Scars',
    blurb: 'Injuries last the WHOLE RUN — only rest-stop cures remove them — and your starting purse is cut to 20 gold.',
  },
  {
    level: 6,
    name: 'Ascension 6 — Long March',
    blurb: 'Another −5 max HP (−15 total), and one row in every act becomes an ELITE GAUNTLET — every path leads through an elite.',
  },
  {
    // A7 ↔ A9 swapped in v1.75 (per Javi): starting injured is the
    // gentler step, so it comes first on the ladder.
    level: 7,
    name: 'Ascension 7 — Rough Start',
    blurb: 'Your starting creatures begin the run INJURED.',
  },
  {
    level: 8,
    name: 'Ascension 8 — Famine',
    blurb: 'Another −5 max HP (−20 total), and missions harden again (+20% total requirements and damage).',
  },
  {
    level: 9,
    name: 'Ascension 9 — Broken Bones',
    blurb: 'Injuries STACK — every failure adds another −10% to everything. Each cure removes a single stack.',
  },
  {
    level: 10,
    name: 'Ascension 10 — Short-Handed',
    blurb: 'Draft one FEWER starting creature.',
  },
]

/** ⚔️ Gauntlet mode (v2.34, per Javi): the rung ABOVE Ascension 10 in
 * the picker. Runs carry every A10 modifier (state.ascension stays 10 —
 * the modifier helpers never see 11) plus state.gauntlet: after the
 * act-3 boss there is NO summit — acts continue forever with compounding
 * requirements (TUNING.acts.gauntletRampPerAct), and the run only ends
 * at 0 HP. Unlocked by BEATING A10 (useAscension stores 11). */
export const GAUNTLET_LEVEL = 11

export const GAUNTLET_INFO: AscensionDef = {
  level: GAUNTLET_LEVEL,
  name: 'The Gauntlet',
  blurb: 'Ascension 10 without a summit: past Act 3 the acts NEVER stop, every act demands dramatically more coverage, and all twelve bosses take their turn. You draft three creatures, as on Ascension 9. The run ends only when you fall — how deep can you go?',
}

export function ascensionInfo(level: number): AscensionDef {
  if (level >= GAUNTLET_LEVEL) return GAUNTLET_INFO
  return ASCENSIONS[Math.max(0, Math.min(MAX_ASCENSION, level))]!
}

/** 🎖️ Progressive ascension badge (v1.19): the emblem grows grander with
 * the level — A1 is a bare triangle, A10 a winged, haloed crest. Levels
 * 1..10 only; Normal (0) has no badge (callers guard with ascension >= 1). */
export function ascensionBadge(level: number): string {
  const n = Math.max(1, Math.min(MAX_ASCENSION, Math.round(level)))
  return `/ui/ascension-${n}.webp`
}

// ---------- Numeric rules (each level includes everything below it) ----------

/** Total max-HP tax: −5 at each of A2 / A4 / A6 / A8. */
export function ascensionMaxHpPenalty(level: number): number {
  return [2, 4, 6, 8].filter(step => level >= step).length * 5
}

/** A2/A8 (v0.80): each step hardens every rolled mission by +10%
 * requirements and damage — ADDITIVE per Javi (v0.82): A2–A7 = +10%,
 * A8+ = +20% flat. Like every ascension modifier, it persists through
 * all higher levels. Applied in generateMission via
 * MissionGenOptions.pressureMult, and to boss statlines at map
 * generation. */
export function ascensionPressureMult(level: number): number {
  return 1 + 0.1 * [2, 8].filter(step => level >= step).length
}

/** A4 (v0.80): completing an act heals only UP TO this share of max HP
 * (a heal never lowers HP you already have). */
export function ascensionActHealPct(level: number): number {
  return level >= 4 ? 0.8 : 1
}

/** A6 (reworked v0.83, per Javi): one full ELITE GAUNTLET row per act —
 * every path leads through an elite (supersedes v0.80's +1 elite, which
 * still left an elite-free path). */
export function ascensionEliteGauntlet(level: number): boolean {
  return level >= 6
}

/** A10 (v0.84): 🤫 a SECRET — beating the act-3 boss reveals a hidden
 * FOURTH boss instead of ending the run. Deliberately absent from the
 * A10 blurb; the discovery is the design. */
export function ascensionSecretBoss(level: number): boolean {
  return level >= 10
}

/** A7 (swapped with A9 in v1.75): the drafted team starts injured. */
export function ascensionStartsInjured(level: number): boolean {
  return level >= 7
}

/** 💰 A5 (v4.13, per Javi): the starting purse (TUNING.player.startingGold,
 * 45 at birth) is cut to 20 from Scars upward. */
export function ascensionStartingGold(level: number, base: number): number {
  return level >= 5 ? 20 : base
}

/** A10: one fewer starting creature. */
export function ascensionRosterPenalty(level: number): number {
  return level >= 10 ? 1 : 0
}

/**
 * 🎲 How many creatures a run drafts, by ascension: Normal 4, A1–A9 3,
 * A10 2 - and the Gauntlet, which runs as A10, drafts A9's THREE
 * (v6.99, per Javi: "mimic ascension 9"; it keeps every other A10 rule).
 *
 * v3.83: lifted out of useRun so the whole ladder is one pure function a
 * test can walk. It was three terms — a base, a Normal-only bonus and an
 * A10 penalty — spread across two files, which is a lot of surface for a
 * rule that is really just those three numbers.
 */
export function ascensionDraftSize(level: number, base: number, gauntlet = false): number {
  const normalBonus = level === 0 ? 1 : 0
  return Math.max(1, base + normalBonus - (gauntlet ? 0 : ascensionRosterPenalty(level)))
}
